import sum from 'lodash/sum';
import type { BufferingMode } from '../../enums';
import type { DestroyOptions, LuminaryObject } from '../../types';
import calculateReadingDurations from '../../libs/calculateReadingDurations';
import { assertHasOwnProperties, assertObject } from '../../validators';
import { Config } from '../../video-data/config';
import { BaseAsset } from '../asset/BaseAsset';
import { ConcurrentTimeline } from '../../libs/ConcurrentTimeline';
import { Subscene } from './Subscene';

export class SubsceneManager extends BaseAsset implements LuminaryObject {
    override _classType = 'SubsceneManager';

    _timeline = new ConcurrentTimeline({
        name: 'subscenes_timeline',
        duration: Config.DEFAULT_SCENE_DURATION,
    });

    /**
     * Flag which indicates loading of properties is in progress
     * We need it to prevent `_checkAndFixProperties` call during `load` process in order to prevent recursive call of`_updateSubsceneDurationBasedOnTextLength`
     */
    #isVideoDataLoading = false;
    /**
     * Duration which we set on scene level.
     * We need it in order not to apply intermediate duration in `_checkAndFixProperties` during updating scene.duration
     */
    _durationToApply: number = null;

    /**
     * Called while seeking, forwarding a request to buffer any data (if need be) down the hierarchy.
     */
    async _propagateBuffering(bufferingMode: BufferingMode) {
        const activeSubscenes = this.slide._getActiveSubscenesAt(this.slide.currentTime);
        await Promise.all(
            activeSubscenes.map(async (subscene) => {
                return subscene._propagateBuffering(bufferingMode);
            }),
        );
    }

    override get canPlay() {
        return this.children.every((asset) => asset.canPlay);
    }

    /**
     * This shouldn't be needed, but adding as the interface requires it. Ideally the
     * SubsceneManager isn't a "luminary object", but that requires a larger refactor.
     */
    _emitAssetsCanPlay() {
        console.warn(
            'SubsceneManager should not be forwarding canPlay events, the subscene' +
                ' should be sending these events to the slide. Forwarding for now anyway.',
        );
        if (this.slide) {
            this.slide.emit('assetsCanPlay');
        }
    }

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     */
    get areAssetsDoneTryingToLoad(): boolean {
        return this.subscenes.every((subscene) => subscene.areAssetsDoneTryingToLoad);
    }

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    override get isLoaded(): boolean {
        return this.children.every((asset) => asset.isLoaded);
    }

    override get _shouldLoad() {
        return true;
    }

    get duration(): number {
        return this.slide.duration;
    }

    /**
     * @deprecated use {@link subscenes} instead
     * */
    get subslides(): Subscene[] {
        return this.subscenes;
    }

    get subscenes(): Subscene[] {
        return this.children as Subscene[];
    }

    /**
     * Create and add a new subscene. By default, it will add the subscene at the end.
     * @param duplicateContent - if content should be copied from the first subscene
     * @param index - if provided, the index where to insert the subscene
     * @param id - if provided, the id of the newly created subscene
     * @returns the newly created Subscene
     */
    addSubscene(duplicateContent = true, index?: number, id?: string, grow = false) {
        const duration = this.duration;
        if (duration === 0) {
            throw Error('Invalid scene duration');
        }
        if (typeof index === 'undefined') {
            index = this.subscenes.length;
        }
        const sourceSubscene = this.subscenes[0];

        const subscene = new Subscene(this, {
            id,
            updateParentOnRedraw: true,
            fetchResources: this.fetchResources,
            timeline: this._globalTimeline,
            autoDraw: this.autoDraw,
            assetNetworkManager: this._assetNetworkManager,
            getAspectRatio: () => this._getAspectRatio(),
            reusableTextures: this._reusableTextures,
        });

        if (this.slide) {
            // Add the new subscene group's timeline to the scene's timeline without changing the scene's duration
            this._timeline.add(subscene._timeline, index, grow);
        }

        // copy base properties
        if (duplicateContent && sourceSubscene) {
            this.#copySubsceneContentFromSourceToTarget(sourceSubscene, subscene);
        }

        // addChild triggers a redraw so we have to do all of the wiring beforehand which is why this is run last.
        this.insertAssetAt(subscene, index);
        return subscene;
    }

    /** @deprecated Use addSubscene instead */
    addSubslide(duplicateContent = true, index?: number, id?: string, grow = false) {
        return this.addSubscene(duplicateContent, index, id, grow);
    }

    /**
     * duplicates our scene's base subscene configuration onto the given subscene
     */
    #copySubsceneContentFromSourceToTarget(sourceSubscene: Subscene, targetSubscene: Subscene) {
        assertObject(sourceSubscene);
        assertObject(targetSubscene);

        // transfer over all child content
        const childrenConfig = sourceSubscene.saveChildren();
        targetSubscene.load(childrenConfig);

        targetSubscene.generateNewAssetIds();

        // update all text content to be blank (clear text)
        // we do this because when duplicating a subscene we don't want to copy over text
        targetSubscene.getAssetsByType(Config.TEXT_ASSET_TYPE).forEach((asset) => {
            asset.text = '';
        });
    }

    removeSubscene(index: number) {
        if (!this.subscenes.hasOwnProperty(index)) {
            return;
        }
        this._timeline.remove(this.subscenes[index]._timeline);
        this.subscenes[index].destroy(true);
    }

    /** @deprecated Use removeSubscene instead */
    removeSubslide(index: number) {
        this.removeSubscene(index);
    }

    generateNewAssetIds() {
        this.subscenes.forEach((subscene) => {
            subscene.generateNewAssetIds();
        });
    }

    override clear() {
        const nsubscenes = this.subscenes.length;
        for (let i = 0; i < nsubscenes; i++) {
            this.removeSubscene(0);
        }
        this._durationToApply = null;
        this.#isVideoDataLoading = false;
    }

    override _checkAndFixProperties(): void {
        super._checkAndFixProperties();

        if (this.#isVideoDataLoading) {
            return;
        }

        if (!this.#hasAssetWithTextEditingEnabled) {
            const duration = this._durationToApply ?? this.duration;
            this._durationToApply = null;
            // in the _build method, we don't want to mess with the autoDraw setting
            // so we set the `batch` parameter to false.
            this._updateSubsceneDurationBasedOnTextLength(duration, false);
        }

        this.#removeAnimationEndAnchorsFromLastSubscene();
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.subscenes.forEach((subscene) => subscene.destroy(options));
    }

    override _build() {}

    get #hasAssetWithTextEditingEnabled() {
        return this.slide
            .getAssetsByType(Config.TEXT_ASSET_TYPE)
            .some((textAsset) => textAsset.hasTextEditingEnabled);
    }

    _updateSubsceneDurationBasedOnTextLength(duration: number, batch = true) {
        const initAutoDraw = this.autoDraw;
        if (batch) {
            this.autoDraw = false;
        }

        const textLengths = this.#getTextLengthArrayFromTextAssetChildren();
        const durations = calculateReadingDurations(textLengths, duration);
        durations.forEach((duration, index) => {
            this.subscenes[index].duration = duration;
        });

        if (batch) {
            this.autoDraw = initAutoDraw;
        }
    }

    #getTextLengthArrayFromTextAssetChildren() {
        return this.subscenes.map((subscene) => {
            return sum(
                subscene
                    .getAssetsByType(Config.TEXT_ASSET_TYPE)
                    .map((textAsset) => textAsset.text.length),
            );
        });
    }

    #removeAnimationEndAnchorsFromLastSubscene() {
        const _applyAnchorsToRemove = (isLastSubscene: boolean, asset: BaseAsset) => {
            if (
                isLastSubscene &&
                asset.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
            ) {
                asset._animationAnchorsToRemove = Config.END_ANIMATION_CURVE_ANCHOR;
            } else {
                asset._animationAnchorsToRemove = Config.NONE_ANIMATION_CURVE_ANCHOR;
            }
        };

        this.subscenes.forEach((subscene, index) => {
            const isLastSubscene = index === this.subscenes.length - 1;
            _applyAnchorsToRemove(isLastSubscene, subscene);
            subscene.getAssets().forEach((child: BaseAsset) => {
                _applyAnchorsToRemove(isLastSubscene, child);
            });
        });
    }

    override load(data: { subslides?: Record<string, unknown>[] } = {}) {
        assertObject(data);
        assertHasOwnProperties(data, ['subslides']);

        if (this.subscenes.length !== 0) {
            throw Error('Unable to load data, subscene length is not zero.');
        }

        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;

        this.#isVideoDataLoading = true;

        data.subslides.forEach((subsceneProperties, index) => {
            const subscene = this.addSubscene(
                false,
                index,
                subsceneProperties.id as string | undefined,
                true,
            );
            subscene.load(subsceneProperties);
        });

        this.#isVideoDataLoading = false;

        this.autoDraw = initAutoDraw;
        return this;
    }

    override save(allProperties = true, clone = false) {
        const data: { subslides: Record<string, unknown>[] } = { subslides: [] };
        this.subscenes.forEach((subscene) =>
            data.subslides.push(subscene.save(allProperties, clone)),
        );
        return data;
    }
}
